/**
 * Left sidebar of the portal shell.
 *
 * Declarative on purpose, mirroring `shared-layout/navigation/aifc-navigation.constants.ts`
 * — the markup used to be six near-identical hardcoded `div`s, which is how the
 * "My Applications" item ended up with a `(click)` handler and no `routerLink`,
 * silently breaking its `routerLinkActive` highlight.
 */
import { Roles } from './modules/expat/models/roles.model';

export interface PortalNavItem {
  /** i18n key; the English label doubles as the key, as everywhere else here. */
  labelKey: string;
  /**
   * Icon name resolved by ng-zorro's `IconService`. Besides Ant's own names,
   * `icon-map.ts` registers a few of ours (`sticky-note-2`, `chat-report`) for slots
   * where the design uses a glyph Ant has no equivalent for.
   */
  icon: string;
  /**
   * Absolute route. Absent = the item is shown but does nothing yet.
   * Absolute rather than relative (`./applications`) because the same list feeds
   * the profile dropdown in the header, which sits outside the portal route.
   */
  routerLink?: string;
  /**
   * Путь раздела на aifc-portal, если он живёт не в этом приложении. Взаимо-
   * исключающий с `routerLink`: уходим по `window.location`, а не роутером.
   */
  externalPath?: string;
  /**
   * Absolute URL prefixes that also light the item up. `routerLinkActive` only
   * knows about `routerLink`, so a page reached *from* this section — an opened
   * application, say — would otherwise drop the highlight entirely and leave the
   * menu looking as if nothing is selected.
   */
  activePaths?: string[];
  /**
   * Feature flag code that must be on for this item to render. The code rather
   * than the name of a component getter: the same list also builds the profile
   * dropdown in the header, which has no access to `PortalHomeComponent`.
   */
  featureFlag?: string;
  /** Name of a `PortalHomeComponent` counter shown as a badge next to the label. */
  badgeCounter?: 'notificationCount';
  /** Renders a separator above this item. */
  separatorBefore?: boolean;
  /** Item stays in the portal sidebar but is left out of the header profile dropdown. */
  hideInProfileMenu?: boolean;
}

export const PORTAL_NAV: PortalNavItem[] = [
  {
    labelKey: 'My applications',
    icon: 'sticky-note-2',
    routerLink: '/portal/applications',
    // Открытая заявка — обзор, форма регистрации, пост-рег события: всё это
    // страницы того же раздела, просто у них свои маршруты.
    activePaths: ['/portal/registration-application', '/portal/post-registration'],
  },
  {
    labelKey: 'Notifications',
    icon: 'bell',
    routerLink: '/portal/notifications',
    featureFlag: 'EXT_NOTIFICATIONS',
    badgeCounter: 'notificationCount',
    hideInProfileMenu: true,
  },
  {
    labelKey: 'Reports',
    icon: 'snippets',
    routerLink: '/portal/reports',
    featureFlag: 'EXT_REPORTS',
  },
  {
    labelKey: 'Payments',
    icon: 'dollar',
    routerLink: '/portal/payments',
    featureFlag: 'EXT_PAYMENTS',
  },
  {
    labelKey: 'Feedback & Reports',
    icon: 'chat-report',
    externalPath: '/account/feedback',
    separatorBefore: true,
  },
  { labelKey: 'Need Assistance?', icon: 'question-circle', routerLink: '/portal/help-support' },
];

/**
 * Ровно то, что нужно для проверки флага. Через интерфейс, а не через импорт
 * `FeatureFlagService`, чтобы этот файл остался чистыми данными без зависимостей
 * от Angular-слоя.
 */
export interface FeatureFlagCheck {
  isEnabled(code: string): boolean;
}

export function isNavItemEnabled(item: PortalNavItem, flags: FeatureFlagCheck): boolean {
  return !item.featureFlag || flags.isEnabled(item.featureFlag);
}

/**
 * Разделы для выпадающего меню профиля: то же боковое меню портала.
 * Живёт здесь, а не в компоненте шапки, потому что меню показывают два
 * разных хоста — портал и лендинг.
 */
export function buildProfileMenuItems(flags: FeatureFlagCheck): PortalNavItem[] {
  return PORTAL_NAV.filter((item) => !item.hideInProfileMenu && isNavItemEnabled(item, flags));
}

/**
 * Личные кабинеты администраторов других разделов aifc-portal (Expat Centre,
 * Property Rental, Marketplace, ...). Показываются только тем, у кого есть
 * соответствующая роль — в отличие от остального списка, эти пункты не видны
 * всем подряд под замком "coming soon", их состав зависит от пользователя.
 */
export interface AdminMenuItem {
  labelKey: string;
  icon: string;
  /** Раздел живёт на aifc-portal, как externalPath у PortalNavItem. */
  externalPath?: string;
  /**
   * Внутренний маршрут front-ext (взаимоисключающий с externalPath): уходим
   * роутером, а не по window.location. Позволяет внутреннему разделу
   * сосуществовать рядом с внешним aifc-portal-аналогом.
   */
  routerLink?: string;
  /**
   * Код feature flag, который должен быть включён, чтобы пункт отображался —
   * тот же контракт, что у PortalNavItem.featureFlag. Пункт без флага виден
   * всегда (при наличии роли); флагованный скрыт, пока флаг выключен.
   */
  featureFlag?: string;
  permission: Roles[];
}

/** Ровно то, что нужно для проверки роли — см. комментарий у FeatureFlagCheck. */
export interface RoleCheck {
  hasRole(role: string): boolean;
}

export const EXPAT_ADMIN_ROLES: Roles[] = [
  Roles.MANAGER_EXPAT,
  Roles.MANAGER_BACKOFFICE,
  Roles.SUPERVISOR,
  Roles.DIRECTOR_EXPAT,
  Roles.GUEST_EXPAT,
  Roles.MANAGER_EXPAT_ALMATY,
  Roles.MANAGER_BACKOFFICE_ALMATY,
  Roles.ONLY_EXPORT_EXPAT
];

export const PROPERTY_ADMIN_ROLES: Roles[] = [
  Roles.PROPERTY_ADMIN,
  Roles.PROPERTY_SUPER_ADMIN
];

export const ADMIN_MENU_ITEMS: AdminMenuItem[] = [
  {
    labelKey: 'Expat Administrator',
    icon: 'user-square',
    routerLink: '/portal/expat-process',
    featureFlag: 'EXT_EXPAT_PROCESS',
    permission: EXPAT_ADMIN_ROLES,
  },
  {
    labelKey: 'Property Administrator',
    icon: 'user-square',
    routerLink: '/portal/property-admin',
    featureFlag: 'EXT_PROPERTY_RENTAL_ADMIN',
    permission: PROPERTY_ADMIN_ROLES,
  },
  {
    labelKey: 'Marketplace Administrator',
    icon: 'user-square',
    externalPath: '/admin/marketplace',
    permission: [Roles.MARKETPLACE_ADMIN],
  },
  {
    labelKey: 'Marketplace Administrator Test',
    icon: 'user-square',
    routerLink: '/portal/marketplace-admin',
    featureFlag: 'EXT_MARKETPLACE_ADMIN',
    permission: [Roles.MARKETPLACE_ADMIN],
  },
  {
    labelKey: 'Data Protection Administrator',
    icon: 'user-square',
    externalPath: '/admin/data-protection',
    permission: [Roles.DATA_PROTECTION_ADMIN, Roles.PROPERTY_ADMIN],
  },
  {
    labelKey: 'Procurement Administrator',
    icon: 'user-square',
    externalPath: '/admin/supplier-registration',
    permission: [Roles.PROCUREMENT_MANAGER],
  },
];

export function buildAdminMenuItems(roles: RoleCheck, flags: FeatureFlagCheck): AdminMenuItem[] {
  return ADMIN_MENU_ITEMS.filter(
    (item) =>
      (!item.featureFlag || flags.isEnabled(item.featureFlag)) &&
      item.permission.some((role) => roles.hasRole(role))
  );
}
